import "./CreateGroup.scss";

import { useContext, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { MenuPartner } from "@/assets/icons/icons";
import { Button } from "@/components/Interface/Button/Button";
import { PortalHeading } from "@/components/Portal/PortalHeading/PortalHeading";
import { useAlert } from "@/hooks/useAlert";
import { useHandleError } from "@/hooks/useHandleError";
import { ErrorType } from "@/mutator/frontendApiInstance";
import { FullScreenLoadingContext } from "@/provider/FullScreenLoadingProvider/FullScreenLoadingProvider";
import { portalRoutes } from "@/router/routes/portalRoutes";
import {
  useGetCustomer,
  useGetCustomerTribe,
  usePostTribe,
} from "@/services/api";
import { AlertTypes } from "@/utils/atoms";

const CreateGroup = () => {
  const { t } = useTranslation("portal", { keyPrefix: "friendsWithBenefits" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const showAlert = useAlert();
  const handleError = useHandleError();
  const { setFullScreenLoading } = useContext(FullScreenLoadingContext);
  const { data: customer } = useGetCustomer();
  const customerName = customer?.firstName;

  const { data: customerTribe } = useGetCustomerTribe();

  useEffect(() => {
    if (customerTribe?.tribeId) {
      // If the customer already has a group, redirect to the group screen
      navigate(portalRoutes.friendsWithBenefits.groupOverview);
    }
  }, [customerTribe, navigate]);

  const { mutate: createTribe } = usePostTribe({
    mutation: {
      onSuccess: () => {
        setFullScreenLoading(false);
        // Navigate to group overview page
        navigate(portalRoutes.friendsWithBenefits.groupOverview);
      },
      onError: (error: ErrorType<unknown>) => {
        setFullScreenLoading(false);
        setIsSubmitting(false);

        if (
          error.response?.data ===
          '400 Bad Request: ""Could not create Tribe : payment type not allowed""'
        ) {
          showAlert({
            text: t("joinGroup.error.paymentMethod"),
            type: AlertTypes.error,
          });
        } else {
          handleError("Failed to create group", t("error.fail"));
        }
      },
    },
  });

  const handleCreateGroup = async () => {
    setIsSubmitting(true);
    setFullScreenLoading(true, t("creatingGroup"));

    createTribe({ tribeName: t("groupName") });
  };

  // Set info in sessionStorage to avoid showing attention bubble a second time
  useEffect(
    () => sessionStorage.setItem("showFriendsWithBenefitsHint", "false"),
    [],
  );

  return (
    <>
      <PortalHeading heading={customerName ? `${customerName},` : ""}>
        <p className="mb-4 mt-8 md:mb-14 md:mt-10">
          <Trans t={t} i18nKey="catchyPhrase" />
        </p>
      </PortalHeading>
      <div
        id="user-watermark-top"
        className="tile-watermark-container !-right-8 xs:hidden"
      >
        <MenuPartner className="h-full w-full" />
      </div>
      <div id="create-group-wrapper" className="tile-width-wrapper">
        <div className="tile-watermark-container">
          <MenuPartner />
        </div>
        <div className="management-tile">
          <div className="smPlus:max-w-[500px]">
            <h2 className="text-secondary-100 md:text-4xl md:leading-[1.33]">
              <Trans t={t} i18nKey="discountInfo" />
            </h2>

            <h3 className="mt-6 font-semibold text-primary-100 md:text-2xl">
              <Trans t={t} i18nKey="startSaving" />
            </h3>

            <div className="mt-12">
              <div className="mt-4">
                <p className="mb-2">
                  <Trans t={t} i18nKey="mainText.1" />
                </p>
                <p className="mb-2">
                  <Trans t={t} i18nKey="mainText.2" />
                </p>
                <p className="mb-2">
                  <Trans t={t} i18nKey="mainText.3" />
                </p>
                <p className="mt-4">
                  <Trans t={t} i18nKey="mainText.additionalInfo" />
                </p>
              </div>
            </div>

            <Button
              className="accent mt-6"
              fullWidth
              onClick={handleCreateGroup}
              disabled={isSubmitting}
            >
              <Trans t={t} i18nKey="createButton" />
            </Button>

            <div className="mt-12">
              <p className="text-base font-medium text-primary-100">
                <Trans t={t} i18nKey="alreadyKnowGroup" />
              </p>
              <p className="text-base">
                <Trans t={t} i18nKey="askForInvite" />
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateGroup;
